/**
 * `SeriesChart` — design §4 step 9's "time series of NDVI composites and CHIRPS months for
 * the selected unit" (`GET uc3/series`). Two small panels in one SVG: NDVI as a line, rain
 * as bars — each on its own scale, since the two are different units and neither series is
 * meant to be read against the other's axis.
 */
import { useRef } from 'react';

import { fmtNum } from '../../../lib/format';
import { AxisLabels } from '../../../components/charts/AxisLabels';
import { useAxisTitles } from '../../../components/charts/axisContext';
import { axisBands } from '../../../components/charts/scale';
import { useChartMarks } from '../../../components/charts/useChartMarks';
import { useIntelWidth } from './layout';

/** The width the chart is laid out at where its box cannot be measured (#749: it measures). */
export const SR_W = 280;
export const SR_PANEL_H = 70;
export const SR_GAP = 14;
export const SR_PAD = 8;

export interface SeriesChartProps {
  ndvi: { date: string; mean: number }[];
  rain: { month: string; total: number }[];
  ndviLabel: string;
  rainLabel: string;
  /** The x axis' title, "quantity (unit)", centred under the chart (launch round). */
  xLabel?: string;
  /** The y axis' title, rotated at the left of the label column. */
  yLabel?: string;
  /** Layout width in px; measured from the chart's own box when omitted (#749). */
  w?: number;
}

/** Each value's point in a panel `w` by `h`, scaled to the series' own range. */
function points(values: number[], w: number, h: number, pad: number): { x: number; y: number }[] {
  if (!values.length) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const step = values.length > 1 ? (w - 2 * pad) / (values.length - 1) : 0;
  return values.map((v, i) => ({
    x: values.length > 1 ? pad + i * step : w / 2,
    y: h - pad - ((v - min) / span) * (h - 2 * pad),
  }));
}

export function SeriesChart({
  ndvi,
  rain,
  ndviLabel,
  rainLabel,
  xLabel: xLabelProp,
  yLabel: yLabelProp,
  w: wProp,
}: SeriesChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { xLabel, yLabel } = useAxisTitles(xLabelProp, yLabelProp);
  /* The titles' bands come out of the measured width; both panels span what is left. */
  const bands = axisBands(xLabel, yLabel);
  const outerW = useIntelWidth(svgRef, wProp, SR_W + bands.left);
  const W = outerW - bands.left;
  const marks = useChartMarks<string>();
  const h = SR_PANEL_H * 2 + SR_GAP;
  const ndviValues = ndvi.map((p) => p.mean);
  const rainValues = rain.map((p) => p.total);
  const maxRain = Math.max(1, ...rainValues);
  const rainW = rain.length ? (W - 2 * SR_PAD) / rain.length : 0;
  const pts = points(ndviValues, W, SR_PANEL_H, SR_PAD);
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const step = pts.length > 1 ? pts[1]!.x - pts[0]!.x : W - 2 * SR_PAD;
  const hot = marks.hot;
  const hotNdvi = hot?.startsWith('n:') ? Number(hot.slice(2)) : null;

  const caption =
    `${ndviLabel}: ${ndvi.map((p) => `${p.date} ${fmtNum(p.mean, 0)}`).join(', ') || '-'}. ` +
    `${rainLabel}: ${rain.map((p) => `${p.month} ${fmtNum(p.total, 0)}mm`).join(', ') || '-'}.`;

  return (
    <svg
      ref={svgRef}
      className={`intel-chart sr-chart${marks.rootClass}`}
      width="100%"
      viewBox={`0 0 ${outerW} ${h + bands.bottom}`}
      role="img"
      aria-label={caption}
      data-testid="series-chart"
      data-fluid=""
      data-w={outerW}
      onMouseLeave={marks.clear}
    >
      <AxisLabels
        xLabel={xLabel}
        yLabel={yLabel}
        plotLeft={bands.left + SR_PAD}
        plotRight={bands.left + W - SR_PAD}
        plotTop={0}
        plotBottom={h}
        height={h + bands.bottom}
      />
      <g transform={bands.left ? `translate(${bands.left} 0)` : undefined}>
      <g data-testid="series-ndvi">
        <text x={SR_PAD} y={12} className="sr-panel-label">
          {ndviLabel}
        </text>
        {ndviValues.length ? (
          <path d={path} fill="none" stroke="var(--brand)" strokeWidth={1.6} />
        ) : null}
        {pts.map((p, i) => (
          <circle key={ndvi[i]!.date} cx={p.x} cy={p.y} r={hotNdvi === i ? 4 : 2.4} fill="var(--brand)" stroke={hotNdvi === i ? '#fff' : undefined} />
        ))}
        {hotNdvi !== null && pts[hotNdvi] ? (
          <line
            className="sr-guide"
            data-guide={ndvi[hotNdvi]!.date}
            pointerEvents="none"
            x1={pts[hotNdvi]!.x}
            x2={pts[hotNdvi]!.x}
            y1={SR_PAD}
            y2={SR_PANEL_H - SR_PAD}
            stroke="var(--ink-2)"
            strokeWidth={1}
            strokeDasharray="2,2"
          />
        ) : null}
        {/* One transparent hit column per composite, over the line (#749). */}
        <g role="list">
          {pts.map((p, i) => {
            const x0 = Math.max(0, p.x - step / 2);
            const x1 = Math.min(W, p.x + step / 2);
            return (
              <rect
                key={ndvi[i]!.date}
                className={`sr-col mark${hotNdvi === i ? ' is-hot' : ''}`}
                data-date={ndvi[i]!.date}
                x={x0}
                y={0}
                width={Math.max(1, x1 - x0)}
                height={SR_PANEL_H}
                fill="transparent"
                {...marks.markProps(`n:${i}`, () => ({ label: `${ndviLabel} · ${ndvi[i]!.date}`, value: fmtNum(ndvi[i]!.mean, 0) }))}
              />
            );
          })}
        </g>
      </g>
      <g transform={`translate(0, ${SR_PANEL_H + SR_GAP})`} data-testid="series-rain" role="list">
        <text x={SR_PAD} y={12} className="sr-panel-label">
          {rainLabel}
        </text>
        {rain.map((p, i) => {
          const barH = (p.total / maxRain) * (SR_PANEL_H - 2 * SR_PAD - 12);
          const x = SR_PAD + i * rainW;
          const y = SR_PANEL_H - SR_PAD - barH;
          const key = `r:${i}`;
          return (
            <rect
              key={p.month}
              className={`mk mark${marks.isHot(key) ? ' is-hot' : ''}`}
              data-month={p.month}
              x={x + 2}
              y={y}
              width={Math.max(2, rainW - 4)}
              height={Math.max(0, barH)}
              fill="var(--blue)"
              {...marks.markProps(key, () => ({ label: `${rainLabel} · ${p.month}`, value: `${fmtNum(p.total, 0)} mm` }))}
            >
              <title>{`${p.month} · ${fmtNum(p.total, 0)} mm`}</title>
            </rect>
          );
        })}
      </g>
      </g>
    </svg>
  );
}

export default SeriesChart;
